import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { byDivision } from "@mpl/shared";
import { useTRPC, type RouterOutput } from "../lib/trpc.js";
import { Card, Empty, PageHeader, Tabs } from "../components/ui.js";
import { Loadable, type QueryLike } from "../components/Loadable.js";
import StandingsTable from "../components/StandingsTable.js";
import PowerRatings from "../components/PowerRatings.js";
import GameRow, { type RecordOf } from "../components/GameRow.js";
import Bracket from "../components/Bracket.js";
import ManagerSchedule, {
  scheduleParticipants,
  type Participant,
} from "../components/ManagerSchedule.js";
import { SeasonRace } from "../components/SeasonRace.js";
import { SeasonPicker } from "../components/SeasonPicker.js";
import { useScope } from "../lib/useScope.js";
import { useSession } from "../lib/session.js";

/**
 * One competition, asked two questions: where does everyone stand, and who
 * plays who.
 *
 * Standings and Schedule used to be separate pages, then four tabs, and are two
 * views again. The race chart and the bracket were tabs of their own; both are
 * ways of reading the table — how it got to be that way, and what it produced —
 * so they sit on it. All three share one schedule query, which is why they cost
 * nothing to keep together.
 */
type StandingsData = RouterOutput["league"]["standings"];
type ScheduleData = RouterOutput["league"]["schedule"];

type View = "standings" | "schedule";

const VIEWS: { value: View; label: string }[] = [
  { value: "standings", label: "Standings" },
  { value: "schedule", label: "Schedule" },
];

export default function Season() {
  const trpc = useTRPC();
  const { slug, resolvedSlug, setSeason, seasons } = useScope();
  const [params, setParams] = useSearchParams();
  const [includePlayoffs, setIncludePlayoffs] = useState(false);

  const setView = (v: View) => {
    const next = new URLSearchParams(params);
    if (v === "standings") next.delete("view");
    else next.set("view", v);
    setParams(next, { replace: true });
  };

  const standings = useQuery(
    trpc.league.standings.queryOptions({ seasonSlug: slug, includePlayoffs }),
  );
  // Fetched once for the page and shared by the schedule, the bracket and the
  // race chart, rather than once per view.
  const schedule = useQuery(trpc.league.schedule.queryOptions({ seasonSlug: slug }));

  const seasonName = standings.data?.season.name ?? schedule.data?.season.name;

  // The standings are already on the page, so a manager's schedule can show
  // each opponent's record without asking the server for it again.
  const records: RecordOf | undefined = useMemo(() => {
    const rows = standings.data?.rows;
    if (!rows) return undefined;
    return new Map(
      rows.map((r) => [r.managerId, { wins: r.wins, losses: r.losses, ties: r.ties }]),
    );
  }, [standings.data?.rows]);

  // Anything else — `?view=table`, and the `race` and `bracket` tabs that are
  // now cards on the standings — lands on the standings, where all three of
  // those things live.
  const requested = params.get("view");
  const view: View = requested === "schedule" ? "schedule" : "standings";


  return (
    <div>
      <PageHeader
        title={seasonName ?? "Season"}
        subtitle="Standings, schedule, and how the season actually unfolded."
        action={
          <div className="flex flex-wrap items-center gap-2">
            <Tabs value={view} onChange={setView} options={VIEWS} />
            <SeasonPicker slug={resolvedSlug} setSeason={setSeason} seasons={seasons} />
          </div>
        }
      />

      {view === "standings" && (
        <StandingsView
          query={standings}
          includePlayoffs={includePlayoffs}
          setIncludePlayoffs={setIncludePlayoffs}
          seasonSlug={slug}
          bracket={schedule.data?.bracket ?? []}
        />
      )}
      {view === "schedule" && <ScheduleView query={schedule} records={records} />}
    </div>
  );
}

function StandingsView({
  query,
  includePlayoffs,
  setIncludePlayoffs,
  seasonSlug,
  bracket,
}: {
  query: QueryLike<StandingsData>;
  includePlayoffs: boolean;
  setIncludePlayoffs: (v: boolean) => void;
  seasonSlug: string | undefined;
  bracket: ScheduleData["bracket"];
}) {
  const [byDivisions, setByDivisions] = useState(true);

  return (
    <Loadable
      query={query}
      card
      empty="No games have been played in this competition yet."
      isEmpty={(d) => d.rows.length === 0}
    >
      {(data) => {
        // Not narrowed to StandingsRowView: `byDivision` needs the fuller
        // shared row, and the table only requires a subset of it.
        const rows = data.rows;
        const hasDivisions = rows.some((r) => r.divisionName);
        const divisions = hasDivisions ? byDivision(rows) : [];
        const slugOf = new Map(rows.map((r) => [r.managerId, r.slug]));
        const powerOf = data.power
          ? new Map(data.power.ratings.map((r) => [r.managerId, r.rating]))
          : undefined;

        return (
          <div className="space-y-6">
            {hasDivisions && (
              <div className="flex justify-end">
                <Tabs
                  value={byDivisions ? "division" : "league"}
                  onChange={(v) => setByDivisions(v === "division")}
                  options={[
                    { value: "division", label: "By division" },
                    { value: "league", label: "League" },
                  ]}
                />
              </div>
            )}

            {hasDivisions && byDivisions ? (
              divisions.map((d) => (
                <Card key={String(d.divisionId)} title={d.divisionName ?? "League"}>
                  <StandingsTable
                    rows={d.rows.map((r) => ({
                      ...r,
                      slug: slugOf.get(r.managerId) ?? null,
                    }))}
                    seeds={data.seeds}
                    power={powerOf}
                  />
                </Card>
              ))
            ) : (
              <Card title="League table">
                <StandingsTable
                  rows={rows}
                  seeds={data.seeds}
                  showDivision={hasDivisions}
                  power={powerOf}
                />
              </Card>
            )}

            {/* Straight under the table for a competition that has one: the
                seeds in that table are what the bracket was drawn from. */}
            {bracket.length > 0 && (
              <Card title="Playoffs">
                <div className="p-4">
                  <Bracket games={bracket} />
                </div>
              </Card>
            )}

            {/* Then how the table came to look like that, and then the rating
                that reorders it. */}
            <SeasonRace seasonSlug={seasonSlug} />

            {data.power && (
              <Card title="Power rating">
                <PowerRatings power={data.power} />
              </Card>
            )}

            <div className="grid gap-6 md:grid-cols-2">
              <Card title="Tiebreakers">
                <ol className="space-y-1 px-4 py-3 text-sm text-chalk-400">
                  {data.tiebreakers.map((t, i) => (
                    <li key={t} className="flex gap-2">
                      <span className="w-4 shrink-0 text-chalk-600">{i + 1}.</span>
                      <span>{t}</span>
                    </li>
                  ))}
                </ol>
                <p className="border-t border-field-800 px-4 py-3 text-xs text-chalk-500">
                  Where two managers are level on record, the criterion that
                  separated them is shown next to their name. A random draw is
                  never performed automatically — if it comes to that, the table
                  falls back to alphabetical order and the Commissioner runs the
                  draw.
                </p>
              </Card>

              <Card title="Reading this table">
                <div className="space-y-2 px-4 py-3 text-sm text-chalk-400">
                  <p>
                    <span className="text-blitz-400">●</span> marks a manager
                    holding a playoff seed. Division winners take seeds 1 and 2;
                    the rest are wildcards.
                  </p>
                  {data.seedsAreProjected ? (
                    <p className="text-xs text-chalk-500">
                      Seeds are projected from the current table and will move as
                      results come in.
                    </p>
                  ) : (
                    <p className="text-xs text-chalk-500">
                      These are the seeds the season actually finished with. Older
                      seasons were seeded under earlier rulebooks, so they may not
                      match what today's tiebreakers would produce.
                    </p>
                  )}
                  <p>
                    <strong className="text-chalk-200">Div</strong> is the record
                    against your own division,{" "}
                    <strong className="text-chalk-200">Strk</strong> the current
                    run of results.
                  </p>
                  <label className="mt-3 flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-field-600 bg-field-950 accent-blitz-500"
                      checked={includePlayoffs}
                      onChange={(e) => setIncludePlayoffs(e.target.checked)}
                    />
                    Include playoff games in the record
                  </label>
                </div>
              </Card>
            </div>
          </div>
        );
      }}
    </Loadable>
  );
}

/**
 * Whose schedule is on screen: everybody's, or one manager's.
 *
 * The signed-in manager comes first and is marked, because "who am I playing
 * next" is the question this filter exists to answer.
 */
function ManagerFilter({
  people,
  value,
  onChange,
  meId,
}: {
  people: Participant[];
  value: string;
  onChange: (slug: string) => void;
  meId: number | null;
}) {
  const me = people.find((p) => p.managerId === meId);
  const rest = people.filter((p) => p.managerId !== meId);

  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="text-chalk-500">Schedule for</span>
      <select
        className="input w-auto py-1.5"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Everyone</option>
        {me?.slug && <option value={me.slug}>{me.name} (you)</option>}
        {rest.map((p) => (
          <option key={p.managerId} value={p.slug ?? ""} disabled={!p.slug}>
            {p.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function ScheduleView({
  query,
  records,
}: {
  query: QueryLike<ScheduleData>;
  records?: RecordOf;
}) {
  const [openWeek, setOpenWeek] = useState<number | null>(null);
  const initialised = useRef<string | null>(null);
  const [params, setParams] = useSearchParams();
  const { user } = useSession();
  const weeks = query.data?.weeks ?? [];

  const managerSlug = params.get("manager") ?? "";
  const setManagerSlug = (slug: string) => {
    const next = new URLSearchParams(params);
    if (slug) next.set("manager", slug);
    else next.delete("manager");
    setParams(next, { replace: true });
  };

  // Open the week that's actually in play the first time a season loads, but
  // leave the user's choice alone after that.
  const liveWeek = useMemo(() => {
    const idx = weeks.findIndex((w) => w.games.some((g) => g.status !== "final"));
    if (idx >= 0) return weeks[idx].number;
    return weeks[weeks.length - 1]?.number ?? null;
  }, [weeks]);

  useEffect(() => {
    const key = query.data?.season.slug ?? null;
    if (key && initialised.current !== key) {
      initialised.current = key;
      setOpenWeek(liveWeek);
    }
  }, [query.data?.season.slug, liveWeek]);

  return (
    <Loadable
      query={query}
      card
      empty="No schedule has been published for this competition yet."
      isEmpty={(d) => d.weeks.length === 0 && d.bracket.length === 0}
    >
      {(data) => {
        const people = scheduleParticipants(data);
        const selected = managerSlug
          ? people.find((p) => p.slug === managerSlug) ?? null
          : null;

        const filter = (
          <ManagerFilter
            people={people}
            value={selected?.slug ?? ""}
            onChange={setManagerSlug}
            meId={user?.id ?? null}
          />
        );

        // A season switch can carry a manager who never played in the new
        // competition, and so can a shared link. Say so rather than showing an
        // empty list that looks like a broken schedule.
        if (managerSlug && !selected) {
          return (
            <div className="space-y-4">
              <div className="flex justify-end">{filter}</div>
              <Card>
                <Empty>
                  That manager didn't take part in this competition.{" "}
                  <button
                    className="text-blitz-400 hover:underline"
                    onClick={() => setManagerSlug("")}
                  >
                    Show everyone
                  </button>
                  .
                </Empty>
              </Card>
            </div>
          );
        }

        if (selected) {
          return (
            <div className="space-y-4">
              <div className="flex justify-end">{filter}</div>
              <ManagerSchedule data={data} manager={selected} records={records} />
            </div>
          );
        }

        return (
          <div className="space-y-6">
            <div
              className={`flex flex-wrap items-center gap-3 ${
                data.weeks.length > 0 ? "justify-between" : "justify-end"
              }`}
            >
              {data.weeks.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {data.weeks.map((w) => {
                    const played = w.games.filter((g) => g.status === "final").length;
                    const complete = w.games.length > 0 && played === w.games.length;
                    return (
                      <button
                        key={w.id}
                        onClick={() => setOpenWeek(w.number)}
                        className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition ${
                          openWeek === w.number
                            ? "border-blitz-500 bg-blitz-500/10 text-blitz-400"
                            : complete
                              ? "border-field-700 bg-field-900 text-chalk-500 hover:text-chalk-200"
                              : "border-field-700 bg-field-850 text-chalk-200 hover:border-field-600"
                        }`}
                      >
                        {/* Bare numbers are fine for "Week 7" but useless for a
                            tournament, where the weeks are Round 1-5 and a Final. */}
                        {w.label && w.label !== `Week ${w.number}` ? w.label : w.number}
                      </button>
                    );
                  })}
                </div>
              )}
              {filter}
            </div>

            {data.weeks
              .filter((w) => openWeek == null || w.number === openWeek)
              .map((w) => (
                <Card
                  key={w.id}
                  title={w.label ?? `Week ${w.number}`}
                  action={
                    w.byes.length > 0 ? (
                      <span className="text-xs text-chalk-500">
                        Bye: {w.byes.map((b) => b.name).join(", ")}
                      </span>
                    ) : null
                  }
                >
                  {w.games.length === 0 ? (
                    <Empty>No games this week.</Empty>
                  ) : (
                    w.games.map((g) => (
                      <GameRow key={g.id} game={g} records={records} />
                    ))
                  )}
                </Card>
              ))}
          </div>
        );
      }}
    </Loadable>
  );
}
